import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Over, Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, StatusTabs, Controls, Toolbar, Search, List, Row, Meta, Empty, Section, TextLink, Loading, FieldLabel, humanize } from "@/components/calm/Calm";
import { fmtDay, daysFromToday } from "@/lib/dates";
import { workspaceKind } from "@/lib/workspace";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";



const EMPTY_CONTACT = { first_name:'', last_name:'', email:'', phone:'', type:'Buyer', source:'', notes:'' };
const EMPTY_TASK = { contact_id:'', title:'', description:'', due_date:'', priority:'normal' };
const EMPTY_ACTIVITY = { contact_id:'', type:'call', description:'' };
const ACTIVITY_TYPES = ['call','email','meeting','note','showing','offer'];
const STAGES = ['new','contacted','qualified','negotiating','closed'];
const STAGE_LABEL = { new:'New', contacted:'Contacted', qualified:'Qualified', negotiating:'Negotiating', closed:'Closed' };

export default function CRMPage() {
  const { resolvedTheme } = useTheme();
  const { session, user } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [activeTab, setActiveTab] = useState('Contacts');
  const [contacts, setContacts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planGate, setPlanGate] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [expandedContact, setExpandedContact] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY);
  const [transactions, setTransactions] = useState([]);
  const [attachDealId, setAttachDealId] = useState('');
  const navigate = useNavigate();
  const kind = workspaceKind(user);
  const billingHref = kind === 'solo' ? '/solo/billing' : kind === 'team_lead' || kind === 'team_member' ? '/team/billing' : '/billing';

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/crm/contacts`, { headers })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (r.status === 403) {
          const detail = typeof d?.detail === 'string' ? d.detail : 'Contacts require the Keystone plan or higher. Upgrade in Billing to unlock.';
          setPlanGate(detail);
          setContacts([]);
        } else {
          setPlanGate(null);
          setContacts(Array.isArray(d) ? d : []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    fetch(`${BASE}/api/crm/tasks`, { headers }).then(r => r.json()).then(d => setTasks(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/crm/activities`, { headers }).then(r => r.json()).then(d => setActivities(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/transactions`, { headers }).then(r => r.json()).then(d => setTransactions(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const apiError = async (res, fallback) => {
    const body = await res.json().catch(() => ({}));
    const d = body.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d.message === 'string') return d.message;
    return fallback;
  };

  const handleAddContact = async () => {
    if (!form.first_name || !form.last_name) return toast.error("First and last name required");
    try {
      const res = await fetch(`${BASE}/api/crm/contacts`, { method: 'POST', headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error(await apiError(res, "Failed to add contact"));
      toast.success("Contact added");
      setShowAdd(false);
      setForm(EMPTY_CONTACT);
      load();
    } catch (e) { toast.error(e.message || "Failed to add contact"); }
  };

  const openEditContact = (c) => {
    setEditing(c);
    setForm({
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      email: c.email || '',
      phone: c.phone || '',
      type: c.type || 'Lead',
      source: c.source || '',
      notes: c.notes || '',
    });
  };

  const handleUpdateContact = async () => {
    if (!editing) return;
    if (!form.first_name || !form.last_name) return toast.error("First and last name required");
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || null,
        phone: form.phone || null,
        type: form.type,
        source: form.source || null,
        notes: form.notes || null,
      };
      const res = await fetch(`${BASE}/api/crm/contacts/${editing.id}`, { method: 'PATCH', headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await apiError(res, "Failed to update contact"));
      toast.success("Contact updated");
      setEditing(null);
      setForm(EMPTY_CONTACT);
      load();
    } catch (e) { toast.error(e.message || "Failed to update contact"); }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact? Linked listings keep their file; the contact is removed.")) return;
    try {
      const res = await fetch(`${BASE}/api/crm/contacts/${contactId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await apiError(res, "Failed to delete contact"));
      toast.success("Contact deleted");
      if (expandedContact === contactId) setExpandedContact(null);
      if (editing?.id === contactId) { setEditing(null); setForm(EMPTY_CONTACT); }
      load();
    } catch (e) { toast.error(e.message || "Failed to delete contact"); }
  };

  const handleAddTask = async () => {
    if (!taskForm.contact_id || !taskForm.title) return toast.error("Contact and title required");
    try {
      const res = await fetch(`${BASE}/api/crm/tasks`, { method: 'POST', headers, body: JSON.stringify(taskForm) });
      if (!res.ok) throw new Error();
      toast.success("Task added");
      setShowAddTask(false);
      setTaskForm(EMPTY_TASK);
      load();
    } catch { toast.error("Failed to add task"); }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const res = await fetch(`${BASE}/api/crm/tasks/${taskId}/complete`, { method: 'PATCH', headers });
      if (!res.ok) throw new Error();
      load();
    } catch { toast.error("Failed to complete task"); }
  };

  const handleLogActivity = async () => {
    if (!activityForm.contact_id || !activityForm.type) return toast.error("Contact and type required");
    try {
      const res = await fetch(`${BASE}/api/crm/activities`, { method: 'POST', headers, body: JSON.stringify(activityForm) });
      if (!res.ok) throw new Error();
      toast.success("Activity logged");
      setShowLogActivity(false);
      setActivityForm(EMPTY_ACTIVITY);
      load();
    } catch { toast.error("Failed to log activity"); }
  };

  const handleStage = async (contactId, stage) => {
    try {
      const res = await fetch(`${BASE}/api/crm/contacts/${contactId}`, { method: 'PATCH', headers, body: JSON.stringify({ stage }) });
      if (!res.ok) throw new Error();
      load();
    } catch { toast.error("Could not update stage"); }
  };

  const handleAttachDeal = async (contactId) => {
    if (!attachDealId) return toast.error("Pick a listing");
    try {
      const res = await fetch(`${BASE}/api/transactions/${attachDealId}`, { method: 'PATCH', headers, body: JSON.stringify({ contact_id: contactId }) });
      if (!res.ok) throw new Error();
      toast.success("Contact attached to listing");
      setAttachDealId('');
      load();
    } catch { toast.error("Could not attach to listing"); }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    const split = (line) => {
      const out = []; let cur = ''; let q = false;
      for (const ch of line) {
        if (ch === '"') { q = !q; continue; }
        if (ch === ',' && !q) { out.push(cur.trim()); cur = ''; continue; }
        cur += ch;
      }
      out.push(cur.trim());
      return out;
    };
    const header = split(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
    const idx = (name, fallback) => {
      const i = header.indexOf(name);
      return i >= 0 ? i : fallback;
    };
    const iFirst = idx('first_name', 0);
    const iLast = idx('last_name', 1);
    const iEmail = idx('email', 2);
    const iPhone = idx('phone', 3);
    const iType = idx('type', 4);
    const start = header.includes('first_name') || header.includes('email') ? 1 : 0;
    const rows = [];
    for (const line of lines.slice(start)) {
      const cols = split(line);
      rows.push({
        first_name: cols[iFirst] || '',
        last_name: cols[iLast] || '',
        email: cols[iEmail] || '',
        phone: cols[iPhone] || '',
        type: cols[iType] || 'lead',
        source: 'CSV',
      });
    }
    return rows;
  };

  const handleImportCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) return toast.error("No rows in that CSV");
    try {
      const res = await fetch(`${BASE}/api/crm/contacts/import`, { method: 'POST', headers, body: JSON.stringify({ contacts: rows }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error();
      const msg = `Imported ${body.imported || 0}` + (body.skipped ? `, skipped ${body.skipped}` : '');
      toast.success(msg);
      load();
    } catch { toast.error("Could not import CSV"); }
  };

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.first_name + ' ' + c.last_name).toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  const TABS = ['Contacts', 'Pipeline', 'Tasks', 'Activity'];

  if (loading) return <Loading />;

  const openTasks = tasks.filter(t => !t.completed);
  const isOverdue = (t) => !t.completed && t.due_date && daysFromToday(t.due_date) < 0;
  const sortedTasks = [...tasks].sort((x, y) => (x.completed - y.completed) || String(x.due_date || '9').localeCompare(String(y.due_date || '9')));
  const importLabel = (
    <label data-testid="crm-import" style={{ fontSize: 13, color: p.ink2, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS }}>
      Import CSV
      <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={e => { handleImportCsv(e.target.files?.[0]); e.target.value = ''; }}/>
    </label>
  );
  const headerAction = planGate ? null
    : activeTab === 'Contacts' ? <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>{importLabel}<Btn variant="primary" dark={dark} icon="plus" onClick={() => setShowAdd(true)}>Add contact</Btn></div>
    : activeTab === 'Tasks' ? <Btn variant="primary" dark={dark} icon="plus" onClick={() => setShowAddTask(true)}>Add task</Btn>
    : activeTab === 'Activity' ? <Btn variant="primary" dark={dark} icon="plus" onClick={() => setShowLogActivity(true)}>Log activity</Btn>
    : null;
  const fieldSel = { marginTop: 6, width: '100%', padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg };

  return (
    <Page testid="crm-page">
      <PageHeader title="Contacts"
        subtitle={planGate ? 'Contacts, pipeline and tasks come with Keystone and above'
          : `${contacts.length} contacts, ${openTasks.length} open tasks`}
        action={headerAction} />

      {planGate && (
        <Empty testid="crm-plan-gate" action={<Btn variant="accent" dark={dark} onClick={() => navigate(billingHref)}>Open billing</Btn>}>
          <div style={{ fontSize: 16, fontWeight: 500, color: p.ink, marginBottom: 6 }}>Contacts need Keystone</div>
          {planGate}
        </Empty>
      )}

      {!planGate && (<>
      <Stats testid="crm-stats" items={[
        { label: 'Contacts', value: contacts.length },
        { label: 'Open tasks', value: openTasks.length },
        { label: 'Overdue', value: tasks.filter(isOverdue).length, attention: tasks.some(isOverdue) },
      ]} />

      <Controls>
        <StatusTabs testid="crm-tabs" label="View" value={activeTab} onChange={setActiveTab}
          options={TABS.map(t => ({ key: t, label: t }))} />
        {activeTab === 'Contacts' && <Toolbar><Search testid="crm-search" value={search} onChange={setSearch} placeholder="Search contacts" /></Toolbar>}
      </Controls>

      {activeTab === 'Contacts' && (
        filtered.length === 0 ? (
          <Empty action={search ? null : <TextLink onClick={() => setShowAdd(true)}>Add your first contact</TextLink>}>
            {search ? 'No contacts match that search.' : 'No contacts yet. Add one or import a CSV.'}
          </Empty>
        ) : (
          <List testid="crm-contacts">
            {filtered.map(c => (
              <div key={c.id}>
                <Row testid="crm-contact-row"
                  onClick={() => setExpandedContact(expandedContact === c.id ? null : c.id)}
                  title={`${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Contact'}
                  meta={[humanize(c.type), c.email, c.phone].filter(Boolean)}
                  next={[c.last_activity_at ? `Last activity ${fmtDay(c.last_activity_at)}` : 'No activity yet', c.lead_score != null ? { text: <>Score <span style={{ fontFamily: F.mono, fontSize: 11.5 }}>{c.lead_score}</span></> } : null].filter(Boolean)}
                  status={STAGE_LABEL[c.stage || 'new'] || humanize(c.stage)} />
                {expandedContact === c.id && (
                  <div style={{ padding: '16px 4px 20px', borderBottom: `1px solid ${p.hair}`, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Meta parts={[c.source ? `Source: ${c.source}` : 'No source', `${tasks.filter(t => t.contact_id === c.id && !t.completed).length} open tasks`]} style={{ fontSize: 13 }} />
                    {(c.deals && c.deals.length) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <FieldLabel>Linked listings</FieldLabel>
                        {c.deals.map(d => <div key={d.id}><TextLink onClick={() => navigate(`/transactions/${d.id}`)}>{d.property_address || d.id}</TextLink></div>)}
                      </div>
                    ) : null}
                    <div className="bx-stack" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr) auto', gap: 10, alignItems: 'end' }}>
                      <div>
                        <FieldLabel>Stage</FieldLabel>
                        <select value={c.stage || 'new'} onChange={e => handleStage(c.id, e.target.value)} style={fieldSel}>
                          {STAGES.map(st => <option key={st} value={st}>{STAGE_LABEL[st]}</option>)}
                        </select>
                      </div>
                      <div>
                        <FieldLabel>Attach to listing</FieldLabel>
                        <select value={attachDealId} onChange={e => setAttachDealId(e.target.value)} style={fieldSel}>
                          <option value="">Select a listing</option>
                          {transactions.map(t => <option key={t.id} value={t.id}>{t.property_address}{t.deal_type ? `, ${humanize(t.deal_type).toLowerCase()}` : ''}</option>)}
                        </select>
                      </div>
                      <Btn variant="ghost" dark={dark} onClick={() => handleAttachDeal(c.id)}>Attach</Btn>
                    </div>
                    {c.notes && <div style={{ fontSize: 13, color: p.ink2, lineHeight: 1.5 }}>{c.notes}</div>}
                    <div style={{ display: 'flex', gap: 18 }}>
                      <TextLink onClick={() => openEditContact(c)}>Edit contact</TextLink>
                      <TextLink onClick={() => handleDeleteContact(c.id)}>Delete</TextLink>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </List>
        )
      )}

      {activeTab === 'Pipeline' && (
        contacts.length === 0 ? <Empty>No contacts in the pipeline yet.</Empty> : (
          <div className="grid grid-cols-1 lg:grid-cols-5" style={{ gap: 24 }}>
            {STAGES.map(st => {
              const col = contacts.filter(c => (c.stage || 'new') === st);
              return (
                <Section key={st} title={<>{STAGE_LABEL[st]} <span style={{ fontFamily: F.mono, fontSize: 12, color: p.ink4, marginLeft: 4 }}>{col.length}</span></>}>
                  {col.length === 0 ? <div style={{ fontSize: 12.5, color: p.ink4, borderTop: `1px solid ${p.hair}`, padding: '12px 0' }}>None</div> : (
                    <List>
                      {col.map(c => (
                        <Row key={c.id} wide={false} onClick={() => { setActiveTab('Contacts'); setExpandedContact(c.id); }}
                          title={`${c.first_name || ''} ${c.last_name || ''}`.trim()}
                          meta={[humanize(c.type), (c.deals || [])[0]?.property_address].filter(Boolean)} />
                      ))}
                    </List>
                  )}
                </Section>
              );
            })}
          </div>
        )
      )}

      {activeTab === 'Tasks' && (
        tasks.length === 0 ? <Empty action={<TextLink onClick={() => setShowAddTask(true)}>Add a task</TextLink>}>No tasks yet.</Empty> : (
          <List testid="crm-tasks">
            {sortedTasks.map(task => (
              <Row key={task.id} testid="crm-task-row"
                title={<span style={{ textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? p.ink3 : undefined }}>{task.title}</span>}
                meta={[task.contact_name, task.description].filter(Boolean)}
                next={task.due_date ? [{ text: isOverdue(task) ? `Overdue, was due ${fmtDay(task.due_date)}` : `Due ${fmtDay(task.due_date)}`, attention: isOverdue(task) }] : null}
                status={task.completed ? 'Done' : task.priority === 'high' ? 'High priority' : 'Open'}
                attention={!task.completed && task.priority === 'high'}
                aside={!task.completed ? <TextLink onClick={() => handleCompleteTask(task.id)}>Complete</TextLink> : null} />
            ))}
          </List>
        )
      )}

      {activeTab === 'Activity' && (
        activities.length === 0 ? <Empty action={<TextLink onClick={() => setShowLogActivity(true)}>Log activity</TextLink>}>No activity recorded yet.</Empty> : (
          <List testid="crm-activity">
            {activities.map(act => (
              <Row key={act.id}
                title={act.contact_name || humanize(act.type) || 'Activity'}
                meta={[act.description].filter(Boolean)}
                next={act.created_at ? [{ text: fmtDay(act.created_at), figure: true }] : null}
                status={humanize(act.type)} />
            ))}
          </List>
        )
      )}

      {/* Add Contact Dialog */}
      </>)}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add contact</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Add someone to your contacts.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><Over color={p.ink3}>First name</Over>
                <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div><Over color={p.ink3}>Last name</Over>
                <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
            </div>
            <div><Over color={p.ink3}>Email</Over>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="name@example.com"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div><Over color={p.ink3}>Phone</Over>
              <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (416) 555-0100"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><Over color={p.ink3}>Contact type</Over>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {['Buyer','Seller','Landlord','Tenant','Lead','Past Client'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div><Over color={p.ink3}>Source</Over>
                <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  <option value="">Select source...</option>
                  {['Referral','Website','MLS','Social Media','Walk-in','Cold Call','Other'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            <div><Over color={p.ink3}>Notes</Over>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything worth remembering"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleAddContact}>Add contact</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setForm(EMPTY_CONTACT); } }}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Edit contact</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Update contact details. Changes save to your office CRM.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><Over color={p.ink3}>First name</Over>
                <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div><Over color={p.ink3}>Last name</Over>
                <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
            </div>
            <div><Over color={p.ink3}>Email</Over>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="name@example.com"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div><Over color={p.ink3}>Phone</Over>
              <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (416) 555-0100"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><Over color={p.ink3}>Contact type</Over>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {['Buyer','Seller','Landlord','Tenant','Lead','Past Client'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div><Over color={p.ink3}>Source</Over>
                <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  <option value="">Select source...</option>
                  {['Referral','Website','MLS','Social Media','Walk-in','Cold Call','Other'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            <div><Over color={p.ink3}>Notes</Over>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything worth remembering"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => { setEditing(null); setForm(EMPTY_CONTACT); }}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleUpdateContact}>Save changes</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add task</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Create a follow-up task tied to a contact.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Contact</Over>
              <select value={taskForm.contact_id} onChange={e => setTaskForm({ ...taskForm, contact_id: e.target.value })}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                <option value="">Select contact...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div><Over color={p.ink3}>Title</Over>
              <input type="text" value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Follow up on offer"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><Over color={p.ink3}>Due date</Over>
                <input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div><Over color={p.ink3}>Priority</Over>
                <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {['low','normal','high'].map(v => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div><Over color={p.ink3}>Description</Over>
              <textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} rows={2} placeholder="Optional detail..."
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowAddTask(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleAddTask}>Add task</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Activity Dialog */}
      <Dialog open={showLogActivity} onOpenChange={setShowLogActivity}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Log activity</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Record an interaction with a contact.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><Over color={p.ink3}>Contact</Over>
              <select value={activityForm.contact_id} onChange={e => setActivityForm({ ...activityForm, contact_id: e.target.value })}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                <option value="">Select contact...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div><Over color={p.ink3}>Type</Over>
              <select value={activityForm.type} onChange={e => setActivityForm({ ...activityForm, type: e.target.value })}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                {ACTIVITY_TYPES.map(v => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
              </select>
            </div>
            <div><Over color={p.ink3}>Description</Over>
              <textarea value={activityForm.description} onChange={e => setActivityForm({ ...activityForm, description: e.target.value })} rows={3} placeholder="What happened..."
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowLogActivity(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleLogActivity}>Log activity</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
